import React, { useState, useEffect, useContext } from "react";
import { AdminDataContext } from "../../../useContext/AdminContext";
import moment from "moment";
import API from "../../../api";
import ProductDetailsModal from "../Modals/ProductDetailsModal";
import "./BCProducts.css";

const BCProducts = () => {
  const { snackbar } = useContext(AdminDataContext);

  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    limit: 10,
  });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_created");
  const [sortOrder, setSortOrder] = useState("desc");

  // Filter states
  const [filters, setFilters] = useState({
    category: "",
    minPrice: "",
    maxPrice: "",
    isVisible: "",
  });

  // UI states
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [categories, setCategories] = useState([]);

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
        productType: "bc", // Always filter for BC products
        ...(search && { search }),
        ...(filters.category && { category: filters.category }),
        ...(filters.minPrice && { minPrice: filters.minPrice }),
        ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
        ...(filters.isVisible !== "" && { isVisible: filters.isVisible }),
      });

      const response = await API.get(`/api/v1/products/products?${params}`);

      if (response.data.success) {
        setProducts(response.data.data.products || []);
        setPagination(
          response.data.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalProducts: 0,
            limit: 10,
          }
        );
      } else {
        snackbar("Failed to fetch products", "error");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      snackbar("Error fetching products", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for filter
  const fetchCategories = async () => {
    try {
      const response = await API.get("/api/v1/category/categories");
      if (response.data.success) {
        const categoriesData =
          response.data.data?.categories || response.data.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  // Initial load - fetch categories once
  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle all data fetching - when page, limit, sort, filters, or search changes
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchProducts();
      },
      search ? 500 : 0
    ); // Debounce only for search, immediate for other changes

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortOrder, filters, search]);

  // Event handlers
  const handleViewProduct = (product) => {
    setSelectedProductId(product.id);
    setViewModalOpen(true);
  };

  const handleRefresh = () => {
    fetchProducts();
  };

  const handleClearFilters = () => {
    setFilters({
      category: "",
      minPrice: "",
      maxPrice: "",
      isVisible: "",
    });
    setSearch("");
    if (page !== 0) {
      setPage(0);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (page !== 0) {
      setPage(0);
    }
  };

  return (
    <div className="bc-products-container">
      {/* Header */}
      <div className="bc-products-header">
        <h1 className="bc-products-title">BigCommerce Products</h1>
        {/* <p className="bc-products-subtitle">
          View and manage your BigCommerce product catalog
        </p> */}
      </div>

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="filter-row">
          {/* Search Field */}
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search products by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="clear-icon"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Sort By */}
          <div className="form-control">
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_created">Sort By: Date Created</option>
              {/* <option value="name">Sort By: Name</option> */}
              <option value="calculated_price">Sort By: Price</option>
              <option value="inventory_level">Sort By: Stock</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="form-control">
            <select
              className="form-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Order: Descending</option>
              <option value="asc">Order: Ascending</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="button-group">
            <button className="btn" onClick={() => setFilterOpen(true)}>
              <span>📋</span>
              Filters
            </button>
            <button className="btn" onClick={handleRefresh}>
              <span>🔄</span>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {(Object.values(filters).some((v) => v !== "") || search) && (
        <div className="active-filters">
          <h3 className="active-filters-title">Active Filters:</h3>
          <div className="chips-container">
            {search && (
              <div className="chip chip-primary">
                Search: "{search}"
                <button
                  className="chip-delete"
                  onClick={() => setSearch("")}
                  aria-label="Remove search filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.category && (
              <div className="chip chip-secondary">
                Category: {filters.category}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("category", "")}
                  aria-label="Remove category filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.minPrice && (
              <div className="chip chip-info">
                Min: ${filters.minPrice}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("minPrice", "")}
                  aria-label="Remove min price filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.maxPrice && (
              <div className="chip chip-info">
                Max: ${filters.maxPrice}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("maxPrice", "")}
                  aria-label="Remove max price filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.isVisible !== "" && (
              <div className="chip chip-success">
                Visible: {filters.isVisible === "true" ? "Yes" : "No"}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("isVisible", "")}
                  aria-label="Remove visibility filter"
                >
                  ✕
                </button>
              </div>
            )}
            <button className="btn btn-small" onClick={handleClearFilters}>
              ✕ Clear All
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <h3 className="empty-state-title">No products found</h3>
            <p className="empty-state-text">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>Price</th>
                  <th>Cost Price</th>
                  <th>Retail Price</th>
                  <th>Stock</th>
                  <th>Visible</th>
                  <th>Created</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product._id || product.id}>
                    <td>{product.id}</td>
                    <td>
                      <div className="product-name-cell">
                        <div
                          className="product-name tooltip"
                          title={product.name}
                        >
                          {product.name}
                          <span className="tooltiptext">{product.name}</span>
                        </div>
                        <div className="product-sku">
                          SKU: {product.sku || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td>
                      {product.sale_price > 0 ? (
                        <div className="price-cell">
                          <div className="price-original">
                            ${product.price?.toFixed(2) || "0.00"}
                          </div>
                          <div className="price-sale">
                            ${product.sale_price?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                      ) : (
                        <div className="price-regular">
                          ${product.price?.toFixed(2) || "0.00"}
                        </div>
                      )}
                    </td>
                    <td>${product.cost_price?.toFixed(2) || "0.00"}</td>
                    <td>${product.retail_price?.toFixed(2) || "0.00"}</td>
                    <td>
                      <span
                        className={`stock-badge ${
                          (product.inventory_level || 0) > 0
                            ? "stock-in"
                            : "stock-out"
                        }`}
                      >
                        {product.inventory_level || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`visible-badge ${
                          product.is_visible ? "visible-yes" : "visible-no"
                        }`}
                      >
                        {product.is_visible ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="date-text">
                      {moment(product.date_created).format("MMM DD, YYYY")}
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        onClick={() => handleViewProduct(product)}
                        aria-label="View product"
                        title="View details"
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      <div className="pagination-info">
        <div className="pagination-text">
          Showing {page * limit + 1} to{" "}
          {Math.min((page + 1) * limit, pagination.totalProducts)} of{" "}
          {pagination.totalProducts} products
        </div>
        <div className="pagination-controls">
          <button
            className="btn btn-small"
            onClick={() => setPage(0)}
            disabled={page === 0}
          >
            First
          </button>
          <button
            className="btn btn-small"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="pagination-text">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            className="btn btn-small"
            onClick={() => setPage(page + 1)}
            disabled={page >= pagination.totalPages - 1}
          >
            Next
          </button>
          <button
            className="btn btn-small"
            onClick={() => setPage(pagination.totalPages - 1)}
            disabled={page >= pagination.totalPages - 1}
          >
            Last
          </button>
          <select
            className="pagination-select"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>

      {/* Filter Dialog */}
      {filterOpen && (
        <div className="modal-overlay" onClick={() => setFilterOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Filters</h2>
              <button
                className="modal-close"
                onClick={() => setFilterOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="filter-grid">
                {/* Category Filter */}
                <div className="form-control">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={filters.category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                  >
                    <option value="">All Categories</option>
                    {Array.isArray(categories) &&
                      categories.map((category) => (
                        <option key={category._id} value={category.category_id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Price Range */}
                <div className="filter-grid-2col">
                  <div className="form-control">
                    <label className="form-label">Min Price</label>
                    <input
                      type="number"
                      className="search-input"
                      placeholder="$"
                      value={filters.minPrice}
                      onChange={(e) =>
                        handleFilterChange("minPrice", e.target.value)
                      }
                    />
                  </div>

                  <div className="form-control">
                    <label className="form-label">Max Price</label>
                    <input
                      type="number"
                      className="search-input"
                      placeholder="$"
                      value={filters.maxPrice}
                      onChange={(e) =>
                        handleFilterChange("maxPrice", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Visibility Filter */}
                <div className="form-control">
                  <label className="form-label">Visibility</label>
                  <select
                    className="form-select"
                    value={filters.isVisible}
                    onChange={(e) =>
                      handleFilterChange("isVisible", e.target.value)
                    }
                  >
                    <option value="">All</option>
                    <option value="true">Visible</option>
                    <option value="false">Hidden</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={handleClearFilters}>
                Clear All
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setFilterOpen(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        productId={selectedProductId}
      />
    </div>
  );
};

export default BCProducts;
